import { X, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { GRACKLE_COLORS, SEMANTIC } from '@/lib/theme'
import type { GlobalAnnouncement as AnnouncementType } from '@/hooks/useGlobalAnnouncements'

interface GlobalAnnouncementProps {
  announcements: AnnouncementType[]
  onDismiss: (id: string) => void
}

/**
 * Persistent banner that displays global announcements (like Closed Won celebrations).
 * Each user can dismiss announcements individually.
 * Uses Pistachio fill with Prussian text per GRK-BRAND-6 success semantic.
 */
export function GlobalAnnouncement({ announcements, onDismiss }: GlobalAnnouncementProps) {
  if (announcements.length === 0) return null

  return (
    <div
      className="lg:pl-60"
      style={{ backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }}
      role="alert"
      aria-live="polite"
    >
      {announcements.map((announcement) => (
        <div
          key={announcement.id}
          className="flex items-center justify-between px-4 py-2 last:border-b-0"
          style={{ borderBottom: `1px solid ${GRACKLE_COLORS.pistachio}` }}
        >
          <div className="flex items-center gap-3">
            <Trophy
              className="h-5 w-5 shrink-0"
              style={{ color: SEMANTIC.success.text }}
              aria-hidden="true"
            />
            <div className="flex flex-wrap items-center gap-x-2">
              <span className="font-semibold">{announcement.title}</span>
              <span style={{ opacity: 0.85 }}>{announcement.message}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 ml-2"
            style={{ color: SEMANTIC.success.text }}
            onClick={() => onDismiss(announcement.id)}
            aria-label="Dismiss announcement"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  )
}
